import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Droplets,
  Calendar,
  Clock,
  MapPin,
  Car,
  Trash2,
  Loader2,
  User,
  Filter,
  Euro,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tag } from 'lucide-react';

function CarWashPricesDialog({ open, onOpenChange, token }) {
  const [vehicles, setVehicles] = useState([]);
  const [defaultAmount, setDefaultAmount] = useState(10);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/car-wash/prices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setVehicles(r.data.vehicles || []);
        setDefaultAmount(r.data.default_amount ?? 10);
        const p = {};
        (r.data.vehicles || []).forEach(v => { p[v.id] = v.car_wash_price ?? ''; });
        setPrices(p);
      })
      .catch(() => toast.error('Failed to load vehicles'));
  }, [open, token]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(prices).forEach(([id, val]) => {
        if (val !== '' && val !== null && !isNaN(parseFloat(val))) payload[id] = parseFloat(val);
      });
      await axios.put(`${API}/car-wash/prices`, { prices: payload }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Car wash prices saved');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="car-wash-prices-dialog">
        <DialogHeader>
          <DialogTitle>Car Wash Prices per Vehicle</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">
          Set the amount recorded for each vehicle. Empty = default €{Number(defaultAmount).toFixed(2)}.
        </p>
        <div className="space-y-2">
          {vehicles.map(v => (
            <div key={v.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded px-3 py-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-800 text-sm">{v.plate_number}</p>
                <p className="text-xs text-slate-400 truncate">{v.model || '—'}{v.is_active === false ? ' · inactive' : ''}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-slate-500 text-sm">€</span>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  className="w-24 h-9"
                  placeholder={String(defaultAmount)}
                  value={prices[v.id] ?? ''}
                  onChange={(e) => setPrices(prev => ({ ...prev, [v.id]: e.target.value }))}
                  data-testid={`price-input-${v.plate_number}`}
                />
              </div>
            </div>
          ))}
          {vehicles.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No vehicles found</p>}
        </div>
        <Button onClick={save} disabled={saving} className="w-full bg-cyan-700 hover:bg-cyan-800" data-testid="save-car-wash-prices">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Save Prices
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function CarWashAdmin() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_entries: 0, total_amount: 0 });
  const [drivers, setDrivers] = useState([]);

  // Filters
  const initialMonth = new Date().toISOString().slice(0, 7);
  const [filterMonth, setFilterMonth] = useState(initialMonth);
  const [filterDriver, setFilterDriver] = useState('all');
  const [filterPlate, setFilterPlate] = useState('');
  const [pricesOpen, setPricesOpen] = useState(false);

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchStats();
    // eslint-disable-next-line
  }, [filterMonth, filterDriver]);

  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = (res.data || []).filter((u) =>
        ['driver', 'admin-driver'].includes(u.role)
      );
      setDrivers(all);
    } catch (e) {
      console.error('Error fetching drivers', e);
    }
  };

  const buildParams = () => {
    const p = {};
    if (filterMonth) p.month = filterMonth;
    if (filterDriver && filterDriver !== 'all') p.driver_id = filterDriver;
    return p;
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/car-wash`, {
        headers: { Authorization: `Bearer ${token}` },
        params: buildParams(),
      });
      setEntries(res.data || []);
    } catch (e) {
      console.error('Error fetching car wash entries:', e);
      toast.error('Failed to load car wash entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/car-wash/summary/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: buildParams(),
      });
      setStats(res.data || { total_entries: 0, total_amount: 0 });
    } catch (e) {
      console.error('Error fetching stats', e);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(
      `Delete car wash entry for ${entry.vehicle_plate} on ${entry.date}? The linked pending €${(entry.amount || 10).toFixed(2)} expense will also be removed.`
    )) return;
    try {
      await axios.delete(`${API}/car-wash/${entry.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Entry deleted');
      fetchEntries();
      fetchStats();
    } catch (e) {
      toast.error('Failed to delete entry');
    }
  };

  const handleMarkAllPaid = async () => {
    const scopeLabel = filterDriver !== 'all'
      ? `for selected driver (${drivers.find(d => d.id === filterDriver)?.name || 'driver'})`
      : 'for all drivers';
    if (!window.confirm(
      `Mark ALL pending Car Wash expenses ${scopeLabel} in ${filterMonth} as PAID? You can always revert any entry back to pending from the Status column.`
    )) return;
    try {
      const params = { month: filterMonth };
      if (filterDriver && filterDriver !== 'all') params.driver_id = filterDriver;
      const res = await axios.post(`${API}/car-wash/mark-pending-paid`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      toast.success(res.data.message || 'Marked as paid');
      fetchEntries();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark entries as paid');
    }
  };

  const handleToggleStatus = async (entry) => {
    const nextStatus = entry.payment_status === 'paid' ? 'pending' : 'paid';
    try {
      await axios.put(
        `${API}/car-wash/${entry.id}/payment-status`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: nextStatus },
        }
      );
      toast.success(`Marked as ${nextStatus}`);
      fetchEntries();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const visible = entries.filter((e) =>
    !filterPlate
      ? true
      : (e.vehicle_plate || '').toUpperCase().includes(filterPlate.toUpperCase())
  );

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8" data-testid="car-wash-admin-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Car Wash Management</h1>
          <p className="text-slate-600 mt-1">Track car wash expenses reimbursed to drivers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setPricesOpen(true)}
            variant="outline"
            className="border-cyan-600 text-cyan-700 hover:bg-cyan-50"
            data-testid="car-wash-prices-btn"
          >
            <Tag className="h-4 w-4 mr-2" /> Prices
          </Button>
          <Button
            onClick={handleMarkAllPaid}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="mark-all-paid-btn"
            disabled={loading || visible.length === 0}
          >
            <Euro className="h-4 w-4 mr-2" /> Mark all pending as Paid
          </Button>
        </div>
      </div>

      <CarWashPricesDialog open={pricesOpen} onOpenChange={setPricesOpen} token={token} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="stat-total-entries">
          <p className="text-sm text-slate-500">Total entries</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{stats.total_entries}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="stat-total-amount">
          <p className="text-sm text-slate-500">Total expense</p>
          <p className="text-3xl font-bold text-cyan-700 mt-1">€{(stats.total_amount || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-lg p-5 shadow-sm" data-testid="stat-pending">
          <p className="text-sm text-amber-700">Pending</p>
          <p className="text-3xl font-bold text-amber-700 mt-1">
            {entries.filter((e) => e.payment_status !== 'paid').length}
          </p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-lg p-5 shadow-sm" data-testid="stat-paid">
          <p className="text-sm text-emerald-700">Paid</p>
          <p className="text-3xl font-bold text-emerald-700 mt-1">
            {entries.filter((e) => e.payment_status === 'paid').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium text-slate-700">
          <Filter className="h-4 w-4" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm">Month</Label>
            <Input
              type="month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="h-11"
              data-testid="filter-month"
            />
          </div>
          <div>
            <Label className="text-sm">Driver</Label>
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger className="h-11" data-testid="filter-driver"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                {drivers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Number plate contains</Label>
            <Input
              placeholder="e.g. ABC"
              value={filterPlate}
              onChange={(e) => setFilterPlate(e.target.value)}
              className="h-11"
              data-testid="filter-plate"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-10">
            <Droplets className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No car wash entries match the filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="car-wash-admin-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Date</th>
                  <th className="px-4 py-3 text-left font-semibold">Time</th>
                  <th className="px-4 py-3 text-left font-semibold">Driver</th>
                  <th className="px-4 py-3 text-left font-semibold">Plate</th>
                  <th className="px-4 py-3 text-left font-semibold">Where</th>
                  <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  <th className="px-4 py-3 text-center font-semibold">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <tr
                    key={e.id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`admin-cw-row-${e.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-slate-700">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        {formatDate(e.date)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-slate-700">
                        <Clock className="h-3 w-3 text-slate-400" />
                        {e.time || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-slate-700">
                        <User className="h-3 w-3 text-slate-400" />
                        {e.driver_name || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 font-medium text-slate-800">
                        <Car className="h-3 w-3 text-cyan-600" />
                        {e.vehicle_plate || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {e.where ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-slate-400" /> {e.where}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 font-semibold text-cyan-700">
                        <Euro className="h-3 w-3" />
                        {(e.amount || 10).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {e.payment_status === 'paid' ? (
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(e)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-medium transition-colors cursor-pointer"
                          data-testid={`status-paid-${e.id}`}
                          title="Click to mark as Pending"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Paid
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(e)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-medium transition-colors cursor-pointer"
                          data-testid={`status-pending-${e.id}`}
                          title="Click to mark as Paid"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          Pending
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(e)}
                        data-testid={`delete-admin-cw-${e.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
